import {Component, DestroyRef, inject, Input, OnDestroy, OnInit} from '@angular/core';
import {
  BokkzVpbDataService,
  CorporateTaxEntity,
  Ontvoeging,
  VpbAangifte
} from "@snelstart-ventures/fiscaal/domain-logic";
import {FormArray, FormControl, FormGroup} from "@angular/forms";
import {SelectFormControlItem, SharedUiFormControlsModule} from '@snelstart/ui-form-controls';
import {debounceTime, filter, Observable, shareReplay, take} from "rxjs";
import {SharedUiButtonModule} from '@snelstart/ui-button';
import {SharedUiDetailsModule} from '@snelstart/ui-details';
import {CommonModule} from '@angular/common';
import {takeUntilDestroyed} from "@angular/core/rxjs-interop";
import {SharedUiDialogModule} from "@snelstart/ui-dialog";

@Component({
  selector: 'snelstart-venture-fiscaal-feature-fiscale-eenheid-ontvoeging',
  templateUrl: './fiscale-eenheid-ontvoeging.component.html',
  styleUrls: ['./fiscale-eenheid-ontvoeging.component.scss'],
  imports: [
    SharedUiFormControlsModule,
    SharedUiButtonModule,
    SharedUiDetailsModule,
    CommonModule,
    SharedUiDialogModule
  ]
})
export class FiscaleEenheidOntvoegingComponent implements OnInit, OnDestroy {

  @Input() public id!: string;
  private dataService = inject(BokkzVpbDataService);
  private destroyRef = inject(DestroyRef);
  public index = 0;
  public dialogVisible = false;

  public optionsJaNee: SelectFormControlItem<boolean>[] = [
    {value: true, label: 'Ja'},
    {value: false, label: 'Nee'},
  ];

  public formGroup = new FormGroup({
    ontvoeging: new FormArray<FormGroup>([])
  });

  readonly corporateTaxEntity$: Observable<CorporateTaxEntity> =
    this.dataService.corporateTaxEntity$.pipe(
      filter((data): data is CorporateTaxEntity => !!data?.sstRelationId),
      takeUntilDestroyed(this.destroyRef),
      shareReplay({bufferSize: 1, refCount: true})
    );

  ngOnInit(): void {
    this.corporateTaxEntity$.pipe(
      take(1),
      takeUntilDestroyed(this.destroyRef),
    ).subscribe((data) => {
      if (!data.aangifte) return;
      const lijst: Ontvoeging[] = data.aangifte.specificatieFiscaleEenheid?.specificatieOntvoeging ?? [];
      const fa = this.ontvoeging;
      while (fa.length > lijst.length) fa.removeAt(fa.length - 1);
      for (let i = 0; i < lijst.length; i++) {
        if (i < fa.length) {
          fa.at(i).patchValue(lijst[i], {emitEvent: false});
        } else {
          fa.push(this.createFormGroup(lijst[i]), {emitEvent: false});
        }
      }
    })
    this.formGroup.valueChanges.pipe(
      debounceTime(300),
      takeUntilDestroyed(this.destroyRef)
    ).subscribe(() => {
      this.saveForm();
    });
  }

  ngOnDestroy(): void {
    this.saveForm();
  }

  get ontvoeging(): FormArray {
    return this.formGroup.get('ontvoeging') as FormArray;
  }

  showDialog(index : number): void {
    this.index = index;
    this.dialogVisible = true;
  }

  addRij(): void {
    this.ontvoeging.push(this.createFormGroup(), { emitEvent: false });
  }

  verwijderRij(index: number): void {
    this.ontvoeging.removeAt(index, { emitEvent: false });
    this.saveForm();
  }

  createFormGroup(item?: Ontvoeging): FormGroup {
    return new FormGroup({
      naamDochter: new FormControl<string | null>(item?.naamDochter ?? null),
      voorDochterOpgeofferdeBedrag: new FormControl<number | null>(item?.voorDochterOpgeofferdeBedrag ?? null),
      waarderingOnderlingeVorderingDochter: new FormControl<number | null>(item?.waarderingOnderlingeVorderingDochter ?? null),
      waarderingOnderlingSchuldDochter: new FormControl<number | null>(item?.waarderingOnderlingSchuldDochter ?? null),
      waarderingOnderlingeVorderingAndereMaatschappij: new FormControl<number | null>(item?.waarderingOnderlingeVorderingAndereMaatschappij ?? null),
      waarderingOnderlingeSchuldAndereMaatschappij: new FormControl<number | null>(item?.waarderingOnderlingeSchuldAndereMaatschappij ?? null),
      toepassing15ai: new FormControl<boolean | null>(item?.toepassing15ai ?? null),

      vermogensbestanddeel15aiInBezitOvernemer: new FormControl<boolean | null>(item?.vermogensbestanddeel15aiInBezitOvernemer ?? null),
      vermogensbestanddeel15aiInBezitOverdrager: new FormControl<boolean | null>(item?.vermogensbestanddeel15aiInBezitOverdrager ?? null),
      wevVermogensbestanddeelBijOntvoeging: new FormControl<number | null>(item?.wevVermogensbestanddeelBijOntvoeging ?? null),
      waarderingVermogensbestanddeelBijTegenbewijs: new FormControl<number | null>(item?.waarderingVermogensbestanddeelBijTegenbewijs ?? null),
      boekwaardeVermogensbestanddeelBijOntvoeging: new FormControl<number | null>(item?.boekwaardeVermogensbestanddeelBijOntvoeging ?? null),
      winstFEdoorOverdrachtVermogensbestanddeel: new FormControl<number | null>(item?.winstFEdoorOverdrachtVermogensbestanddeel ?? null),

      vormingHIRdoorOvernemerVanToepassing: new FormControl<boolean | null>(item?.vormingHIRdoorOvernemerVanToepassing ?? null),
      aanwendingHIRdoorOvernemer: new FormControl<boolean | null>(item?.aanwendingHIRdoorOvernemer ?? null),
      winstFEvanwegeVrijvalHIR: new FormControl<number | null>(item?.winstFEvanwegeVrijvalHIR ?? null),
      wevVervangendeBedrijfsmiddelBijOntvoeging: new FormControl<number | null>(item?.wevVervangendeBedrijfsmiddelBijOntvoeging ?? null),
      boekwaardeVervangendeBedrijfsmiddelBijOntvoeging: new FormControl<number | null>(item?.boekwaardeVervangendeBedrijfsmiddelBijOntvoeging ?? null),
      winstFeDoorHerwaarderingVervangendeBedrijfsmiddel: new FormControl<number | null>(item?.winstFeDoorHerwaarderingVervangendeBedrijfsmiddel ?? null),
      hirOpBalansOntvoegdeDochter: new FormControl<boolean | null>(item?.hirOpBalansOntvoegdeDochter ?? null),
      vervangingsvoornemenOntvoegdeDochter: new FormControl<boolean | null>(item?.vervangingsvoornemenOntvoegdeDochter ?? null),
    });
  }

  saveForm(): void {
    this.formGroup.updateValueAndValidity({emitEvent: false});
    const specificatieOntvoeging: Ontvoeging[] = this.ontvoeging.getRawValue()
      .filter((row: any) => row.naamDochter)
      .map((row: any): Ontvoeging => ({
        naamDochter: row.naamDochter ?? undefined,
        voorDochterOpgeofferdeBedrag: row.voorDochterOpgeofferdeBedrag ?? undefined,
        waarderingOnderlingeVorderingDochter: row.waarderingOnderlingeVorderingDochter ?? undefined,
        waarderingOnderlingSchuldDochter: row.waarderingOnderlingSchuldDochter ?? undefined,
        waarderingOnderlingeVorderingAndereMaatschappij: row.waarderingOnderlingeVorderingAndereMaatschappij ?? undefined,
        waarderingOnderlingeSchuldAndereMaatschappij: row.waarderingOnderlingeSchuldAndereMaatschappij ?? undefined,
        toepassing15ai: row.toepassing15ai ?? undefined,
        vermogensbestanddeel15aiInBezitOvernemer: row.vermogensbestanddeel15aiInBezitOvernemer ?? undefined,
        vermogensbestanddeel15aiInBezitOverdrager: row.vermogensbestanddeel15aiInBezitOverdrager ?? undefined,
        wevVermogensbestanddeelBijOntvoeging: row.wevVermogensbestanddeelBijOntvoeging ?? undefined,
        waarderingVermogensbestanddeelBijTegenbewijs: row.waarderingVermogensbestanddeelBijTegenbewijs ?? undefined,
        boekwaardeVermogensbestanddeelBijOntvoeging: row.boekwaardeVermogensbestanddeelBijOntvoeging ?? undefined,
        winstFEdoorOverdrachtVermogensbestanddeel: row.winstFEdoorOverdrachtVermogensbestanddeel ?? undefined,
        vormingHIRdoorOvernemerVanToepassing: row.vormingHIRdoorOvernemerVanToepassing ?? undefined,
        aanwendingHIRdoorOvernemer: row.aanwendingHIRdoorOvernemer ?? undefined,
        winstFEvanwegeVrijvalHIR: row.winstFEvanwegeVrijvalHIR ?? undefined,
        wevVervangendeBedrijfsmiddelBijOntvoeging: row.wevVervangendeBedrijfsmiddelBijOntvoeging ?? undefined,
        boekwaardeVervangendeBedrijfsmiddelBijOntvoeging: row.boekwaardeVervangendeBedrijfsmiddelBijOntvoeging ?? undefined,
        winstFeDoorHerwaarderingVervangendeBedrijfsmiddel: row.winstFeDoorHerwaarderingVervangendeBedrijfsmiddel ?? undefined,
        hirOpBalansOntvoegdeDochter: row.hirOpBalansOntvoegdeDochter ?? undefined,
        vervangingsvoornemenOntvoegdeDochter: row.vervangingsvoornemenOntvoegdeDochter ?? undefined,
      }));

    this.dataService.patchCorporateTaxEntity(currentEntity => {
      if (!currentEntity.aangifte) return currentEntity;
      return {
        ...currentEntity,
        aangifte: {
          ...currentEntity.aangifte,
          specificatieFiscaleEenheid: {
            ...currentEntity.aangifte.specificatieFiscaleEenheid,
            specificatieOntvoeging,
          },
        } as VpbAangifte,
      };
    });
  }
}
